import moment from 'moment';
import { models } from 'powerbi-client';
import { batch } from 'react-redux';

import { handleErrors } from './ErrorActions';
import { forceSavedSearchRefresh } from './SavedSearchesActions';
import {
  exportPowerBIReports as exportPowerBIReportsApi,
  getDatasetList,
  getEmbedReport,
  getExportStatus as getExportStatusApi,
  getPowerBIImportStatus as getPowerBIImportStatusApi,
  getReportCreationEmbedData,
  getReportsList,
  initiatePowerBIImport as initiatePowerBIImportApi,
} from '../api/powerbi';
import {
  EXPORT_POLLING_TIME,
  IMPORT_POLLING_TIME,
  IMPORT_STEPS,
  POWERBI_ENTITY_NAME,
  STATUSES,
} from '../constants/PowerBIConstants';
import { POWERBI_EDITOR_PAGE } from '../constants/Urls';
import { getPagePathname } from '../utils/childrenWindowsUtils';
import { DATE_FORMAT_WITH_MINUTES_TIMESTAMP } from '../utils/dateUtils';
import { downloadFile } from '../utils/domUtils';
import { getErrorMessage } from '../utils/importUtils';
import { getSelectedReportIds } from '../utils/powerBIUtils';

export const SET_POWERBI_IMPORT_STEP = 'SET_POWERBI_IMPORT_STEP';
export const INITIATE_POWERBI_IMPORT_PROCESS = 'INITIATE_POWERBI_IMPORT_PROCESS';
export const SET_POWERBI_IMPORT_MODAL_OPEN = 'SET_POWERBI_IMPORT_MODAL_OPEN';
export const SET_CREATE_REPORT_MODAL_OPEN = 'SET_CREATE_REPORT_MODAL_OPEN';
export const SET_POWERBI_IMPORT_LOADING = 'SET_POWERBI_IMPORT_LOADING';
export const SET_POWERBI_IMPORT_ERROR = 'SET_POWERBI_IMPORT_ERROR';
export const CLEAR_CURRENT_IMPORT_RECORD_DATA = 'CLEAR_CURRENT_IMPORT_RECORD_DATA';
export const SAVE_DATASET_LIST = 'SAVE_DATASET_LIST';
export const SAVE_REPORTS_LIST = 'SAVE_REPORTS_LIST';
export const CLEAR_REPORT_CREATION_DATA = 'CLEAR_REPORT_CREATION_DATA';
export const SET_EXPORT_PENDING = 'SET_EXPORT_PENDING';
export const SET_REFRESH_DATA = 'SET_REFRESH_DATA';
export const CLEAR_POWERBI_STORE = 'CLEAR_POWERBI_STORE';

export const initiatePowerBIImportProcess = () => ({
  type: INITIATE_POWERBI_IMPORT_PROCESS,
});

export const clearPowerBIStore = () => ({
  type: CLEAR_POWERBI_STORE,
});

export const clearReportCreationData = () => ({
  type: CLEAR_REPORT_CREATION_DATA,
});

export const setPowerBiImportModalOpen = (flag, recordId = '') => ({
  type: SET_POWERBI_IMPORT_MODAL_OPEN,
  payload: { flag, recordId },
});

export const setCreateReportModalOpen = (flag, reportData = {}) => ({
  type: SET_CREATE_REPORT_MODAL_OPEN,
  payload: { flag, reportData },
});

export const setPowerBIImportStep = (importStep, recordId) => ({
  type: SET_POWERBI_IMPORT_STEP,
  payload: { recordId, importStep },
});

export const clearCurrentImportRecordData = () => ({
  type: CLEAR_CURRENT_IMPORT_RECORD_DATA,
});

export const setRefreshData = (refreshDataObj) => ({
  type: SET_REFRESH_DATA,
  payload: { refreshDataObj },
});

const setExportPending = (flag) => ({
  type: SET_EXPORT_PENDING,
  payload: { flag },
});

const setPowerBIImportLoading = (flag, recordId) => ({
  type: SET_POWERBI_IMPORT_LOADING,
  payload: { flag, recordId },
});

const saveDatasetList = (datasetList) => ({
  type: SAVE_DATASET_LIST,
  payload: { datasetList },
});

const saveReportsList = (reportsList) => ({
  type: SAVE_REPORTS_LIST,
  payload: { reportsList },
});

const setPowerBIImportError = (flag, message, recordId) => ({
  type: SET_POWERBI_IMPORT_ERROR,
  payload: { flag, message, recordId },
});

const handleImportApiCallFail = ({ errors, dispatch, recordId }) => {
  const errorMessage = getErrorMessage(errors, 'Error encountered during import. Please try again');

  batch(() => {
    dispatch(setPowerBIImportError(true, errorMessage, recordId));
    dispatch(setPowerBIImportLoading(false, recordId));
  });
};

export const checkImportStatus = ({ importId, status, recordId, dispatch, fileName }) => {
  switch (status) {
    case STATUSES.IN_PROGRESS: {
      setTimeout(async () => {
        getPowerBIImportStatus({
          importId,
          dispatch,
          recordId,
          fileName,
        });
      }, IMPORT_POLLING_TIME);
      return;
    }

    case STATUSES.COMPLETED: {
      batch(() => {
        dispatch(setPowerBIImportStep(IMPORT_STEPS.UPLOAD_COMPLETED, recordId));
        dispatch(setPowerBIImportLoading(false, recordId));
        dispatch(forceSavedSearchRefresh(POWERBI_ENTITY_NAME, true));
      });
      return;
    }

    default: {
      batch(() => {
        dispatch(setPowerBIImportError(true, 'Unexpected Status', recordId));
        dispatch(setPowerBIImportStep(IMPORT_STEPS.UPLOAD_COMPLETED, recordId));
        dispatch(setPowerBIImportLoading(false, recordId));
      });
      return;
    }
  }
};

export const getPowerBIImportStatus = async ({ importId, dispatch, recordId, fileName }) => {
  dispatch(setPowerBIImportStep(IMPORT_STEPS.FILE_PROCESSING, recordId));

  const response = await getPowerBIImportStatusApi(importId, fileName, recordId);

  if (response.hasError) {
    handleImportApiCallFail({ errors: response.errors, dispatch, recordId });
    return;
  }

  const { status } = response;
  checkImportStatus({ status, recordId, dispatch, importId, fileName });
};

export const initiatePowerBIImport = (file, recordId) => async (dispatch) => {
  batch(() => {
    dispatch(setPowerBIImportLoading(true, recordId));
    dispatch(setPowerBIImportStep(IMPORT_STEPS.START_IMPORT, recordId));
  });

  const response = await initiatePowerBIImportApi(file, recordId);

  if (response.hasError) {
    if (response.isTopBanner) {
      dispatch(handleErrors(response.errors, { isTopBanner: true }));
    }

    handleImportApiCallFail({ errors: response.errors, dispatch, recordId });
    return;
  }

  const { ImportId } = response.result;
  getPowerBIImportStatus({ importId: ImportId, dispatch, recordId, fileName: file.name });
};

export const fetchDatasetList = () => async (dispatch) => {
  const response = await getDatasetList();

  if (response.hasError) {
    dispatch(handleErrors(response.errors));
    return;
  }

  dispatch(saveDatasetList(response.datasets));
};

export const fetchReportsList = () => async (dispatch) => {
  const response = await getReportsList();

  if (response.hasError) {
    dispatch(handleErrors(response.errors));
    return;
  }

  dispatch(saveReportsList(response.reports));
};

export const fetchReportConfig = ({
  initialConfig,
  datasetId,
  reportId,
  itmId,
  useUnpublished,
}) => async (dispatch) => {
  //when report is created based on selected dataset
  if (datasetId) {
    const response = await getReportCreationEmbedData({ datasetId });

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return { hasError: true };
    }

    const { embedUrl, embedToken } = response.report;

    return {
      ...initialConfig,
      datasetId,
      accessToken: embedToken,
      embedUrl,
    };
  }

  //when report is created based on selected report
  if (reportId) {
    const response = await getReportCreationEmbedData({ reportId });

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return { hasError: true };
    }

    const { embedUrl, embedToken } = response.report;

    return {
      ...initialConfig,
      viewMode: models.ViewMode.Edit,
      permissions: models.Permissions.Copy,
      datasetId,
      accessToken: embedToken,
      embedUrl,
    };
  }

  //when report is in edit mode
  if (getPagePathname() === POWERBI_EDITOR_PAGE) {
    const response = await getEmbedReport(itmId, true, true);

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return { hasError: true };
    }

    const { embedUrl, embedToken } = response.report;

    return {
      ...initialConfig,
      datasetId,
      accessToken: embedToken,
      embedUrl,
      viewMode: models.ViewMode.Edit,
      permissions: models.Permissions.ReadWrite,
    };
  }

  //when report is in preview/view mode
  const reportConfigResponse = await getEmbedReport(itmId, useUnpublished);

  if (reportConfigResponse.hasError) {
    dispatch(handleErrors(reportConfigResponse.errors));
    return { hasError: true };
  }

  const { embedUrl, embedToken, refreshDate } = reportConfigResponse.report;

  refreshDate && dispatch(setRefreshData({ refreshDate }));

  return {
    ...initialConfig,
    datasetId,
    accessToken: embedToken,
    embedUrl,
  };
};

const handleExportApiFail = (errors, dispatch) => {
  dispatch(handleErrors(errors));
  dispatch(setExportPending(false));
};

export const checkExportStatus = (status, fileName, statuses, dispatch) => {
  switch (status) {
    case STATUSES.IN_PROGRESS: {
      setTimeout(() => {
        getExportStatus(statuses, fileName, dispatch);
      }, EXPORT_POLLING_TIME);
      return;
    }

    default: {
      handleExportApiFail({ message: 'Unexpected Error. Files can not be exported' }, dispatch);
      return;
    }
  }
};

export const getExportStatus = async (statuses, fileName, dispatch) => {
  const { data, headers } = await getExportStatusApi(statuses);

  const contentType = headers['content-type'];

  if (contentType === 'application/json') {
    const reader = new FileReader();

    reader.addEventListener('loadend', () => {
      const response = JSON.parse(reader.result);

      if (data.hasError) {
        handleExportApiFail(data.errors, dispatch);
        return;
      }

      checkExportStatus(response.Status, fileName, statuses, dispatch);
    });

    reader.readAsText(data);
    return;
  }

  const currentDate = moment(new Date()).format(DATE_FORMAT_WITH_MINUTES_TIMESTAMP);
  downloadFile(`${fileName} ${currentDate}`, data, 2);

  dispatch(setExportPending(false));
};

export const exportPowerBIReports = (selectedReports, exportType) => async (dispatch) => {
  dispatch(setExportPending(true));

  const selectedReportIds = getSelectedReportIds(selectedReports);
  const fileName =
    selectedReports.length === 1
      ? selectedReports[0].QI_REPORT_TITLE
      : 'Quality Intelligence Reports';

  const response = await exportPowerBIReportsApi(selectedReportIds, exportType);

  if (response.hasError) {
    handleExportApiFail(response.errors, dispatch);
    return;
  }

  getExportStatus(response.result, fileName, dispatch);
};
